import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { GraduationCap, Award } from "lucide-react";

interface SkillsAnalysis {
  topSkills: Array<{ name: string; count: number; avgYearsExperience?: number }>;
  uniqueSkills: string[];
  totalSkillsMentioned: number;
}

interface EducationAnalysis {
  topUniversities: Array<{ name: string; count: number }>;
  degreeTypes: Record<string, number>;
  fieldOfStudy: Array<{ field: string; count: number }>;
}

interface TeamSkillsMatrixProps {
  skillsAnalysis?: SkillsAnalysis;
  educationAnalysis?: EducationAnalysis;
}

export const TeamSkillsMatrix = ({ skillsAnalysis, educationAnalysis }: TeamSkillsMatrixProps) => {
  if (!skillsAnalysis && !educationAnalysis) {
    return null;
  }

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Skills Analysis */}
      {skillsAnalysis && skillsAnalysis.topSkills.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Award className="h-5 w-5 text-primary" />
              <CardTitle>Najważniejsze Umiejętności</CardTitle>
            </div>
            <CardDescription>
              Top {skillsAnalysis.topSkills.length} umiejętności w zespole
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {skillsAnalysis.topSkills.map((skill, index) => {
                const maxCount = skillsAnalysis.topSkills[0].count;
                const percentage = (skill.count / maxCount) * 100;
                
                return (
                  <div key={index} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{skill.name}</span>
                        {skill.avgYearsExperience && (
                          <span className="text-xs text-muted-foreground">
                            (śr. {skill.avgYearsExperience} lat)
                          </span>
                        )}
                      </div>
                      <Badge variant="secondary">{skill.count}</Badge>
                    </div>
                    <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 p-3 bg-muted/50 rounded-lg">
              <p className="text-sm">
                <span className="font-semibold">Łącznie:</span>{' '}
                {skillsAnalysis.totalSkillsMentioned} unikalnych umiejętności w zespole
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Education Analysis */}
      {educationAnalysis && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-primary" />
              <CardTitle>Wykształcenie Zespołu</CardTitle>
            </div>
            <CardDescription>
              Najczęstsze uczelnie i kierunki studiów
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Top Universities */}
            {educationAnalysis.topUniversities.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-3">Top Uczelnie</h4>
                <div className="space-y-2">
                  {educationAnalysis.topUniversities.slice(0, 5).map((uni, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 rounded-lg bg-card border"
                    >
                      <span className="text-sm">{uni.name}</span>
                      <Badge variant="outline">{uni.count}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Field of Study */}
            {educationAnalysis.fieldOfStudy.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-3">Kierunki Studiów</h4>
                <div className="flex flex-wrap gap-2">
                  {educationAnalysis.fieldOfStudy.slice(0, 8).map((field, index) => (
                    <Badge key={index} variant="secondary">
                      {field.field} ({field.count})
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Degree Types */}
            {Object.keys(educationAnalysis.degreeTypes).length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-3">Typy Dyplomów</h4>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(educationAnalysis.degreeTypes).map(([degree, count]) => (
                    <div
                      key={degree}
                      className="p-2 rounded-lg bg-muted/50 text-center"
                    >
                      <p className="text-lg font-bold">{count}</p>
                      <p className="text-xs text-muted-foreground">{degree}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
